import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { FaStar, FaCheckCircle, FaSearch, FaFilter } from 'react-icons/fa';
import './Brokers.css';

const Brokers = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const [filters, setFilters] = useState({
    regulation: '',
    instantWithdrawal: false,
    minRating: 0
  });

  const [searchTerm, setSearchTerm] = useState('');

  // This would typically come from an API or database
  const brokers = [
    {
      id: 1,
      name: "Global Trade Markets",
      regulation: "FCA, CySEC",
      rating: 4.8,
      instantWithdrawal: true,
      minDeposit: "$100",
      platforms: ["MT4", "MT5", "WebTrader"],
      description: "A well-regulated broker with excellent withdrawal processing times and competitive spreads.",
      fees: "Low",
      support: "24/7",
      established: 2010,
      logo: "GTM"
    },
    {
      id: 2,
      name: "CryptoCap Brokers",
      regulation: "ASIC, FMA",
      rating: 4.6,
      instantWithdrawal: true,
      minDeposit: "$50",
      platforms: ["MT4", "cTrader", "Mobile App"],
      description: "Specialized in cryptocurrency trading with fast withdrawals and advanced charting tools.",
      fees: "Competitive",
      support: "24/5",
      established: 2015,
      logo: "CCB"
    },
    {
      id: 3,
      name: "European Financial Partners",
      regulation: "FCA, BaFin",
      rating: 4.5,
      instantWithdrawal: false,
      minDeposit: "$250",
      platforms: ["MT5", "Proprietary"],
      description: "Highly regulated European broker with advanced trading tools and research resources.",
      fees: "Moderate",
      support: "24/5",
      established: 2008,
      logo: "EFP"
    },
    {
      id: 4,
      name: "Asia Pacific Trading",
      regulation: "MAS, FSA",
      rating: 4.7,
      instantWithdrawal: true,
      minDeposit: "$100",
      platforms: ["MT4", "cTrader", "WebTrader"],
      description: "Leading Asian broker with multilingual support and fast execution speeds.",
      fees: "Low",
      support: "24/7",
      established: 2012,
      logo: "APT"
    },
    {
      id: 5,
      name: "American Capital Markets",
      regulation: "CFTC, NFA",
      rating: 4.4,
      instantWithdrawal: true,
      minDeposit: "$500",
      platforms: ["MT4", "MT5", "Proprietary"],
      description: "US-regulated broker with extensive educational resources and market analysis.",
      fees: "Moderate",
      support: "24/6",
      established: 2005,
      logo: "ACM"
    },
    {
      id: 6,
      name: "Middle East Investments",
      regulation: "DFSA, SCB",
      rating: 4.3,
      instantWithdrawal: false,
      minDeposit: "$200",
      platforms: ["MT5", "WebTrader", "Mobile App"],
      description: "Regional specialist with Sharia-compliant accounts and competitive Islamic financing.",
      fees: "Competitive",
      support: "24/5",
      established: 2018,
      logo: "MEI"
    }
  ];

  const regulations = ["FCA", "CySEC", "ASIC", "FMA", "BaFin", "MAS", "FSA", "CFTC", "NFA", "DFSA", "SCB"];

  const handleFilterChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFilters({
      ...filters,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const filteredBrokers = brokers
    .filter(broker => {
      // Search filter
      if (searchTerm && 
          !broker.name.toLowerCase().includes(searchTerm.toLowerCase()) &&
          !broker.regulation.toLowerCase().includes(searchTerm.toLowerCase())) {
        return false;
      }
      
      // Regulation filter
      if (filters.regulation && !broker.regulation.includes(filters.regulation)) {
        return false;
      }
      
      // Instant withdrawal filter
      if (filters.instantWithdrawal && !broker.instantWithdrawal) {
        return false;
      }
      
      // Minimum rating filter
      if (filters.minRating > 0 && broker.rating < filters.minRating) {
        return false;
      }
      
      return true;
    });

  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6 } }
  };

  const staggerChildren = {
    visible: { 
      transition: { 
        staggerChildren: 0.1 
      } 
    }
  };

  const floatAnimation = {
    y: [0, -5, 0],
    transition: {
      duration: 2,
      repeat: Infinity,
      ease: "easeInOut"
    }
  };

  return (
    <div className="page-content">
      <div className="page-header">
        <div className="container">
          <motion.h1
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            Regulated Brokers
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            Browse our carefully vetted list of regulated brokers with instant withdrawal capabilities
          </motion.p>
        </div>
      </div>
      
      <div className="container">
        <motion.div 
          className="brokers-filters"
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={fadeInUp}
          ref={ref}
        >
          <div className="search-bar">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search brokers or regulations..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          
          <div className="filter-actions">
            <div className="filter-group">
              <FaFilter className="filter-icon" />
              <select 
                value={filters.regulation}
                onChange={handleFilterChange}
                name="regulation"
              >
                <option value="">All Regulators</option>
                {regulations.map(reg => (
                  <option key={reg} value={reg}>{reg}</option>
                ))}
              </select>
            </div>
            
            <div className="filter-group">
              <label className="checkbox-container">
                <input
                  type="checkbox"
                  name="instantWithdrawal"
                  checked={filters.instantWithdrawal}
                  onChange={handleFilterChange}
                />
                <span className="checkmark"></span>
                Instant Withdrawal Only
              </label>
            </div>
            
            <div className="filter-group">
              <select 
                value={filters.minRating}
                onChange={handleFilterChange}
                name="minRating"
              >
                <option value={0}>Any Rating</option>
                <option value={4}>4+ Stars</option>
                <option value={4.5}>4.5+ Stars</option>
                <option value={4.8}>4.8+ Stars</option>
              </select>
            </div>
          </div>
        </motion.div>
        
        <motion.div 
          className="brokers-grid staggered-animations"
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
          variants={staggerChildren}
        >
          {filteredBrokers.length > 0 ? (
            filteredBrokers.map(broker => (
              <motion.div 
                className="broker-card" 
                key={broker.id}
                variants={fadeInUp}
                whileHover={{ 
                  y: -10,
                  boxShadow: "0 20px 25px -5px rgba(0, 0, 0, 0.5), 0 10px 10px -5px rgba(0, 0, 0, 0.4)",
                  borderColor: "#D4AF37",
                  transition: { duration: 0.3 }
                }}
              >
                <div className="broker-header">
                  <motion.h3
                    animate={floatAnimation}
                  >
                    {broker.name}
                  </motion.h3>
                  <div className="broker-rating">
                    <div className="rating-stars">
                      {'★'.repeat(Math.floor(broker.rating))}
                      {'☆'.repeat(5 - Math.floor(broker.rating))}
                    </div>
                    <span className="rating-value">{broker.rating}</span>
                  </div>
                </div>
                
                <div className="broker-details">
                  <div className="detail-row">
                    <span className="detail-label">Regulation:</span>
                    <span className="detail-value">{broker.regulation}</span>
                  </div>
                  
                  <div className="detail-row">
                    <span className="detail-label">Instant Withdrawal:</span>
                    <span className={`detail-value ${broker.instantWithdrawal ? 'positive' : 'negative'}`}>
                      {broker.instantWithdrawal ? <FaCheckCircle /> : 'No'}
                      {broker.instantWithdrawal ? ' Yes' : ''}
                    </span>
                  </div>
                  
                  <div className="detail-row">
                    <span className="detail-label">Minimum Deposit:</span>
                    <span className="detail-value">{broker.minDeposit}</span>
                  </div>
                  
                  <div className="detail-row">
                    <span className="detail-label">Platforms:</span>
                    <span className="detail-value">{broker.platforms.join(', ')}</span>
                  </div>
                  
                  <div className="detail-row">
                    <span className="detail-label">Fees:</span>
                    <span className="detail-value">{broker.fees}</span>
                  </div>
                  
                  <div className="detail-row">
                    <span className="detail-label">Support:</span>
                    <span className="detail-value">{broker.support}</span>
                  </div>
                  
                  <div className="detail-row">
                    <span className="detail-label">Established:</span>
                    <span className="detail-value">{broker.established}</span>
                  </div>
                </div>
                
                <p className="broker-description">{broker.description}</p>
                
                <div className="broker-actions">
                  <motion.button 
                    className="btn btn-brand"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    View Details
                  </motion.button>
                  <motion.button 
                    className="btn btn-outline"
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Compare
                  </motion.button>
                </div>
              </motion.div>
            ))
          ) : (
            <div className="no-results">
              <p>No brokers found matching your criteria. Try adjusting your filters.</p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Brokers;